import _ from "lodash";
import React from "react";
import { store } from "store/createStore";
import classes from "UnitPlans/components/UnitPlans.scss";
import { Tag } from "@toddle-edu/ds-web";
import { SettingsOutlined, UnitPlanOutlined } from "@toddle-edu/ds-icons";
import { fontStyles } from "@toddle-edu/ds-theme";
import {
  PLACEHOLDER_RECT_IMAGE,
  PLACEHOLDER_RECT_IMAGE_COUNT,
  UNIT_TYPE_WITH_UNIT_SUFFIX_LOCALES,
  colors,
} from "Constants";
import moment from "moment";
import {
  FIELD_UID_CURRICULUM_MAPPING,
  CARD_VIEW,
  TIMELINE_VIEW,
  COURSE_OVERVIEW,
  CARD,
  TIMELINE,
  TABLE,
} from "UnitPlans/modules/UnitPlanModule/UnitPlanConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import * as Sentry from "@sentry/browser";
import {
  formatDate,
  getArchivedText,
  getMemoizedLangFunction,
  getPYPElementSetFromNodes,
  generateNestedSelectedData,
  checkIsNodeTopParent,
} from "Utils";
import {
  getPlannerElementLabel,
  checkEmptyElementValue,
  getBenchMarkValueMemoized,
} from "modules/ElementValueUtils";
import { pypElementMapping } from "Constants/plannerElementMappings";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import { getEmptyFieldValueStatus, getPropertyValue } from "Fields/FieldUtils";
import {
  ASSESSMENT,
  ASSIGNMENT_TYPE_LE,
  ASSIGNMENT_TYPE_PERFORMANCE_TASK,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  SUBJECT_TYPE_GENERIC,
  PYP_TRANSDISCIPLINARY,
  UBD_INQUIRY,
  MYP_INTERDISCIPLINARY,
  CURRICULUM_TYPE_PYP,
  PYP_SUBJECT_SPECIFIC,
  UBD_SUBJECT_SPECIFIC,
  MYP_SUBJECT_SPECIFIC,
  UNIT_TYPE_DP,
  CORE_AND_OTHER_TYPES,
  PLAN_TYPE_PRO,
  PLAN_TYPE_PRO_PILOT,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_DP_AIM,
  ELEMENT_TYPE_DP_OBJECTIVES,
  ELEMENT_TYPE_DP_CP_PPS_LEARNING_OUTCOMES,
} from "Constants/stringConstants";
import {
  ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE,
  TODDLE_PLAY_PERMISSION,
  UNIT_PLAN_MORE_DETAILS_IN_BANNER_PERMISSION,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  UNIT_PLAN_EDIT_PERMISSION,
} from "Constants/permissionConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getUnitPlanCentralRepoInputAndFilters,
  mapleBearLikeOrg,
} from "Courses/modules/utils";
import { getUnitPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import { canImportUnitFromToddleLibrary } from "ToddleLibrary/modules/ToddleLibraryUtils";
import update from "immutability-helper";
import { getSettingValue } from "modules/PermissionModule";
import {
  getIsBlueprintCourse,
  getIsLearningCoursesEnabled,
  getIsCourseBasedGradingEnabled,
} from "LearningCourses/modules/Utils";
import {
  getLearningCourseVariables,
  getSelectedLearningCourse,
  getSelectedLearningCourseId,
} from "LearningCourse/modules/Utils";
import { getIsCoursePlanRouteEnabled } from "Utils/ACLStoreUtils";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import ACLStorage from "lib/aclService";
import { getPlannerElementFromCache } from "./UnitPlanGraphqlHelpers";
import { FieldComponentContext } from "Projects/modules/ProjectModules";

export const LE_FEED_STYLES = {
  feedInnerContainerStyle: {
    width: "100%",
    margin: "0 auto",
  },
  headerContainerStyle: {
    padding: "32px 32px 20px",
    width: "100%",
    margin: "0 auto",
  },
  feedContainerStyle: {
    alignItems: "center",
    padding: "0 20px 0 32px",
  },
  customNonSelectedContainerStyle: {
    borderRadius: "8px",
  },
  customSelectedContainerStyle: {
    borderRadius: "8px",
  },
  moreFiltersBarStyle: {
    marginTop: "12px",
  },
  titleTextStyle: {
    margin: 0,
    flexShrink: 0,
  },
  imageContainerStyle: {
    height: "72px",
    width: "72px",
    minWidth: "72px",
  },
  feedItemTitleStyle: {
    fontSize: "1.6rem",
    ...fontStyles.demiBold,
    marginBottom: 0,
  },
  staffDetailsStyle: {
    marginTop: "12px",
  },
  staffNameStyle: {
    ...fontStyles.medium,
    fontSize: "1.2rem",
  },
  feedDataListContainerStyle: {
    height: "100px",
  },
  headerTabContainerStyle: {
    marginBottom: 0,
  },
  customSearchBarStyle: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
  },
  profileImageContainerProps: {
    size: "x-small",
    className: classes.profileImageContainerClass,
  },
  displayCountHeadingWithSearch: true,
};

export const getResourceNodes = ({
  templateId,
  resources,
  curriculumType,
  nodeName,
  isUnitFlowResources = false,
  getCurrentAssessmentTypes,
}) => {
  if (curriculumType !== CURRICULUM_TYPE_UBD) {
    return _.map(resources, resource => {
      const resourceNode = _.get(resource, nodeName, {});
      return isUnitFlowResources ? resource : resourceNode;
    });
  }
  //In case of assessments for UBD we use the assessmentTypes from the template
  const currentAssessmentTypes = getCurrentAssessmentTypes();

  const assessmentTypes = _.map(currentAssessmentTypes, (value, key) =>
    _.assign({ value: key }, value)
  );

  return _.map(resources, resource => {
    const resourceNode = _.get(resource, nodeName, {});
    const isWorksheet = _.isEqual(
      _.get(resourceNode, "assessmentType.value"),
      "worksheet"
    );
    const isNonAssessmentTypeResource =
      !_.isEqual(resource.resourceType, ASSESSMENT) &&
      !_.isEqual(resource.type, ASSESSMENT);
    const isLE = _.isEqual(
      _.get(
        resourceNode,
        "assessmentType.value",
        ASSIGNMENT_TYPE_PERFORMANCE_TASK
      ),
      ASSIGNMENT_TYPE_LE
    );
    // changes should only be applied if resource type is assessment & assessment is not LE.
    if (isWorksheet || isNonAssessmentTypeResource || isLE) {
      return isUnitFlowResources ? resource : resourceNode;
    }

    const newResourceNode = {
      ...resourceNode,
      assessmentType: _.find(assessmentTypes, {
        value: _.get(
          resourceNode,
          "assessmentType.value",
          ASSIGNMENT_TYPE_PERFORMANCE_TASK
        ),
      }),
      isUbdAssessment: true,
    };

    return isUnitFlowResources
      ? {
          ...resource,
          [nodeName]: newResourceNode,
        }
      : newResourceNode;
  });
};

export const getCollaborators = ({
  unitPlanId,
  selectedCourseId,
  academicYearId,
}) => {
  const userInfo = getUserInfo();
  const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
    organizationId: _.get(userInfo, "org_id", ""),
    courseId: selectedCourseId,
    academicYearId,
  });

  const responseFilter = getUnitPlanFieldResponseFilter();
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const unitPlanData = getUnitPlanDetailsFromCache({
    unitPlanId,
    centralRepoInput,
    responseFilter,
    collaboratorsFilter,
    exceptionalCollaboratorsFilter: collaboratorsFilter,
  });

  return _.get(unitPlanData, "collaborators", []);
};

const getUnitPlanStepsLocally = params => {
  const {
    unitPlanDetails,
    stageKey,
    stage_list,
    step_list,
    field_list,
    fieldListV2,
    unitPlanFields,
    removeFirstStep,
    isFirstStage,
  } = params;
  const stageStepIdList = _.get(stage_list, `${stageKey}.steps`, []);

  const filteredStepIdList =
    removeFirstStep && isFirstStage && !_.isEmpty(stageStepIdList)
      ? stageStepIdList.slice(1)
      : stageStepIdList;

  const stageSteps = _.pick(step_list, filteredStepIdList);

  const stepList = _.reduce(
    stageSteps,
    (result, step, key) => {
      const showInUnitSummary = _.get(step, "showInUnitSummary", true);
      const fields = _.filter(_.get(step, "fields", []), field => {
        const isPortedField = _.has(fieldListV2, field);

        if (isPortedField) {
          const fieldObj = fieldListV2[field];
          const uiType = getPropertyValue({
            fieldObj,
            propertyPath: "uiType",
          });

          // don't show IBReflection type fields in unit summary
          if (uiType === "IBReflection") {
            return false;
          }

          return !getEmptyFieldValueStatus({
            fieldObj,
            data: unitPlanDetails,
            fieldKey: field,
          });
        }

        const fieldObj = _.get(field_list, field, {});

        // don't show IBReflection type fields in unit summary
        if (_.get(fieldObj, "type", "") === "IBReflection") {
          return false;
        }
        //added the below check since we are returning true in checkEmptyElementValue for the
        // following types "conceptsQuestion", "actionQuestion", "studentAction" which includes
        // the above mentioned steps even if they are empty
        if (
          _.includes(
            [
              "conceptsQuestion",
              "actionQuestion",
              "studentAction",
              "relatedConcepts",
              "globalContextMYP",
              "theme",
            ],
            field
          )
        ) {
          const unitPlanFieldData = _.find(unitPlanFields, { uid: field });
          const unitPlanFieldValue = _.get(unitPlanFieldData, "value", "");
          const allFields = _.unionBy(
            _.get(unitPlanDetails, "allFields", []),
            _.get(unitPlanDetails, "fields", []),
            "id"
          );
          const dataToFind = _.find(allFields, value => {
            return _.isEqual(_.get(value, "uid"), field);
          });
          const valueKeys = _.get(dataToFind, "value", "");
          const resolvedValue = _.get(dataToFind, "resolvedMinimalTree", "");
          const subjects = _.find(unitPlanFields, { uid: "subjects" });
          const subjectIds = _.get(subjects, "value", []);
          const value = getElementValue({
            fieldUID: field,
            unitPlanFieldValue,
            valueKeys,
            isUnitPlan: true,
            resolvedValue,
            fieldObj,
            subjectIds,
            unitPlanFields,
          });

          return !_.isEmpty(value);
        }

        return checkEmptyElementValue({
          fieldKey: field,
          data: unitPlanDetails,
          unitPlanFields,
          fieldObj,
          isUnitPlan: true,
          ignoreUnitPlanSubjectCheck: getIsCourseBasedGradingEnabled(),
          getElementValue,
        });
      });

      if (fields.length > 0 && showInUnitSummary) {
        result.push(key);
      }
      return result;
    },
    []
  );
  // returns array of step id which have atleast one field filled.
  return stepList;
};

export const getDefaultUnitPlanImage = ({ id, imageUrl, getRethumb }) => {
  return !_.isEmpty(imageUrl)
    ? getRethumb(imageUrl)
    : `url(${PLACEHOLDER_RECT_IMAGE[id % PLACEHOLDER_RECT_IMAGE_COUNT]})`;
};

//created to provide default image for avatars and no rethumb is used because avatars handles it by default
export const getDefaultUnitPlanImageForAvatars = ({ id, imageUrl }) => {
  return !_.isEmpty(imageUrl)
    ? imageUrl
    : PLACEHOLDER_RECT_IMAGE[id % PLACEHOLDER_RECT_IMAGE_COUNT];
};

export const getUnitPlanStepsMemoized = _.memoize(
  params => getUnitPlanStepsLocally(params),
  params => JSON.stringify(params)
);

const getUnitPlanStagesLocally = params => {
  const {
    unitPlanDetails,
    unitSummaryModules,
    module_list,
    stage_list,
    step_list,
    field_list,
    fieldListV2,
    unitPlanFields,
    removeFirstStep,
  } = params;

  //making a copy to return only nonEmpty stages nd steps
  const updatedStageListCopy = _.cloneDeep(stage_list);

  return _.reduce(
    unitSummaryModules,
    (result, module) => {
      const stages = _.get(module_list, `${module}.stages`, []);

      _.forEach(stages, (stageKey, index) => {
        const isFirstStage = index === 1;
        const steps = getUnitPlanStepsMemoized({
          stageKey,
          stage_list,
          step_list,
          field_list,
          fieldListV2,
          unitPlanFields,
          unitPlanDetails,
          removeFirstStep,
          isFirstStage,
        });

        //if steps are not empty we are updating it in updatedStageListCopy
        if (!_.isEmpty(steps)) {
          result.push(stageKey);
          updatedStageListCopy[stageKey].steps = steps;
        }
      });

      const updatedStageList = _.reduce(
        result,
        (obj, key) => {
          obj[key] = updatedStageListCopy[key];
          return obj;
        },
        {}
      );

      return { updatedStageList };
    },
    []
  );
};

export const getUnitPlanStagesMemoized = _.memoize(
  params => getUnitPlanStagesLocally(params),
  params => JSON.stringify(params)
);

const getSharedSections = ({ unitPlanDetails, shareWithModalType }) => {
  const enabledSections = _.find(
    _.get(unitPlanDetails, "enabledSections", []),
    { userType: shareWithModalType }
  );

  return _.get(enabledSections, "sections", []);
};

export const getSharedSectionsMemoized = _.memoize(
  params => getSharedSections(params),
  params => JSON.stringify(params)
);

export const getYearlyUnitPlans = ({ edges }) => {
  return _.map(edges, edge => {
    return {
      id: edge.node.id,
      startDate: edge.node.startDate,
      endDate: edge.node.endDate,
      unitPlan: edge.node,
    };
  });
};

export const getYearlyUnitPlansMemoize = _.memoize(
  params => getYearlyUnitPlans(params),
  params => JSON.stringify(params)
);

export const getFlatUnitPlans = ({ edges }) => {
  return _.map(edges, edge => edge.node);
};

const getSharedDetailsKey = sharedDetails => {
  if (_.isEmpty(sharedDetails)) return "";

  return _.chain(sharedDetails)
    .map(item => {
      const classId = _.get(item, "class.id", "");
      const studentIds = _.chain(item).get("students", []).map("id").value();
      return `class_${classId}_students_${studentIds.join("_")}`;
    })
    .join("|")
    .value();
};

export const getFlatUnitPlansMemoize = _.memoize(
  params => getFlatUnitPlans(params),
  ({ edges }) =>
    JSON.stringify({
      unitPlans: _.map(edges, edge => ({
        id: edge?.node?.id,
        updatedAt: edge?.node?.updatedAt,
        fields: edge?.node?.fields,
        classDates: edge?.node?.classDates ?? null,
        isLocked: edge?.node?.lockingInfo?.isLocked ?? null,
        isUnitFlowLocked: edge?.node?.isUnitFlowLocked ?? null,
        learningCourseFlowResource: _.get(
          edge,
          "node.learningCourseFlowResource"
        )
          ? {
              sharedDetailsKey: getSharedDetailsKey(
                _.get(edge, "node.learningCourseFlowResource.sharedDetails")
              ),
            }
          : null,
        hasLockedGradingPeriodAssessment:
          edge?.node?.hasLockedGradingPeriodAssessment ?? false,
        unitFlowItemCount: edge?.node?.flowResourceCount?.totalCount ?? 0,
        isConflictExistsWithBluePrintCourse:
          edge?.node?.isConflictExistsWithBluePrintCourse ?? false,
        isEditPermission: edge?.node?.collaborators?.isEditPermission ?? true,
        isSyncedWithAllTeacherCourseV2:
          edge?.node?.isSyncedWithAllTeacherCourseV2 ?? null,
        isSharedWithFamilies: edge?.node?.isSharedWithFamilies ?? false,
        isSharedWithStudents: edge?.node?.isSharedWithStudents ?? false,
        enabledSections: edge?.node?.enabledSections ?? [],
      })),
    })
);

export const getFlatUnitPlansForCache = ({ newEdges, edgesInCache }) => {
  return _.map(edgesInCache, (edge, index) => {
    return {
      ...edge,
      node: {
        ...newEdges[index],
      },
    };
  });
};

export const getTeachingHoursMemoize = _.memoize(
  params => getTeachingHours(params),
  params => JSON.stringify(params)
);

export const getLELibraryLabels = ({
  assessmentType,
  assessmentTypeDetail,
  labelType,
  t,
}) => {
  if (labelType === "searchText") {
    return t("common:search");
  }

  switch (assessmentType) {
    case "ASSESSMENT": {
      return t("unitPlan:add_assessment_item_plural");
    }
    case "LEARNING_ENGAGEMENT": {
      return t("unitPlan:add_le_label_item_plural");
    }
    case "SECOND_STAGE_ASSESSMENT": {
      return t("unitPlan:add_stage_2_assessment_item_plural");
    }
    case "THIRD_STAGE_ASSESSMENT": {
      return t("unitPlan:add_stage_3_assessment_item_plural");
    }
    case "WORKSHEET": {
      return t("unitPlan:add_worksheet_item_plural");
    }
    case "AI_TUTOR": {
      return t("unitPlan:add_ai_tutor_item_plural");
    }
    default: {
      return t("unitPlan:add_library_item", {
        label: t(assessmentTypeDetail.label),
      });
    }
  }
};

export const getLELibraryLabelsMemoize = _.memoize(
  params => getLELibraryLabels(params),
  params => JSON.stringify(params)
);

/** This function is to get bar color based on current date and unit start and end date.
 *
 * @param startDate = unit starting date
 * @param endDate = unit ending date
 *
 */

export const getBarColor = ({ startDate, endDate }) => {
  const today = moment();
  if (moment(startDate).isBefore(today) && moment(endDate).isBefore(today)) {
    return colors.teal600;
  } else if (
    moment(startDate).isBefore(today) &&
    moment(endDate).isAfter(today)
  ) {
    return colors.violet600;
  } else if (
    moment(startDate).isAfter(today) &&
    moment(endDate).isAfter(today)
  ) {
    return colors.pink600;
  } else {
    return colors.neutral200;
  }
};

export const getTeachingHours = ({ unitPlans, label }) => {
  return _.reduce(
    unitPlans,
    (sumTeachingHours, unit) => {
      const fields = _.get(unit, "fields", []);
      const teachingHours = parseInt(
        _.get(_.find(fields, { uid: "hours" }), "value", 0)
      );

      if (label == "totalHours") {
        return sumTeachingHours + teachingHours;
      }
      const subjectLevelTags = _.filter(
        _.get(
          _.find(fields, { uid: "subjectLevel" }),
          "resolvedMinimalTree.genericTags"
        ),
        item => item.label === label
      );
      if (!_.isEmpty(subjectLevelTags)) {
        return sumTeachingHours + teachingHours;
      }
      return sumTeachingHours;
    },
    0
  );
};

export const getIsFromClassroom = ({ sourceFrom, template }) => {
  const classroomEditSteps = _.get(template, "classroomEditSteps", []);
  return _.isEqual(sourceFrom, "CLASSROOM") && !_.isEmpty(classroomEditSteps);
};

export const getGroupedCoursesByRegion = courses => {
  if (!_.get(courses, "length", 0)) {
    return [];
  }

  const NORMALIZED_KEY = "REGION";
  const EXCLUDED_VALUES = ["GLOBAL"];

  const normalizedCourses = {};

  _.forEach(courses, course => {
    const tags = _.get(course, "tags", []);

    _.forEach(tags, tag => {
      if (tag.key === NORMALIZED_KEY) {
        if (!_.includes(EXCLUDED_VALUES, tag.value)) {
          const tagValue = normalizedCourses[tag.value];
          if (tagValue) {
            tagValue.courses.push(course);
          } else {
            normalizedCourses[tag.value] = {
              region: tag,
              courses: [course],
            };
          }
        }
      }
    });
  });

  return _.map(Object.keys(normalizedCourses).sort(), regionName => {
    return normalizedCourses[regionName];
  });
};

export const getOptionsForNonPlannerFields = ({
  fieldKey,
  unitPlanFields,
  unitPlanFieldList,
  fieldObj,
}) => {
  const fieldType = _.get(fieldObj, "type", null);

  switch (fieldType) {
    case "CheckListWithPopup": {
      const unitPlanFieldObj = unitPlanFieldList[fieldKey];
      const subFields = _.get(unitPlanFieldObj, "subFields", []);

      const options = {};

      _.forEach(subFields, ({ config, key }) => {
        const filteredBy = _.get(config, "filteredBy", []);

        let resolvedMinimalTree = {};
        _.forEach(filteredBy, fieldUid => {
          resolvedMinimalTree = _.get(
            _.find(unitPlanFields, ({ uid }) => uid === fieldUid),
            "resolvedMinimalTree",
            {}
          );
        });
        options[key] = resolvedMinimalTree;
      });

      return options;
    }
  }
};

export const getDataOptionsForCheckListWithPopup = ({
  unitPlanFields,
  fieldKey,
  unitPlanFieldList,
  dataField,
}) => {
  const fieldConfig = unitPlanFieldList[fieldKey];
  const fieldValue = _.get(
    _.find(unitPlanFields, ({ uid }) => uid === fieldKey),
    "value",
    null
  );
  const updatedValue = getElementValue({
    fieldObj: fieldConfig,
    valueKeys: fieldValue,
  });

  return updatedValue[dataField];
};

/**
 * This Util will return view and edit mode data for popup subFields
 */
const getPopupDataForCheckListWithPopup = ({
  unitPlanFields,
  fieldKey,
  subFields,
  unitPlanFieldList,
  options,
  dataField,
}) => {
  const unitPlanFieldObj = unitPlanFieldList[fieldKey];
  const unitPlanSubFields = _.get(unitPlanFieldObj, "subFields", []);
  let subFieldValueById = _.reduce(
    unitPlanSubFields,
    (result, { key }) => {
      return {
        ...result,
        [key]: { editModeValues: [], viewModeValues: [] },
      };
    },
    {}
  );
  const subFieldObjById = _.reduce(
    unitPlanSubFields,
    (result, fieldObj) => {
      return { ...result, [fieldObj.key]: fieldObj };
    },
    {}
  );
  const windowConfig = _.get(unitPlanFieldObj, "config.windowConfig", []);
  const windowConfigGroupedByWindowGroup = _.groupBy(
    windowConfig,
    ({ windowGroupId }) => windowGroupId
  );

  const value = _.get(
    _.find(unitPlanFields, ({ uid }) => uid === fieldKey),
    "value",
    null
  );

  const subjectIds = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "value",
    ""
  );

  const subFieldIds = _.map(subFields, ({ key }) => key);

  const popupDataById = {};

  _.forEach(value, ({ tableRowsValues }) => {
    const sortedTableRowsValues = _.sortBy(
      tableRowsValues,
      ({ windowGroupId }) => windowGroupId
    );
    _.forEach(sortedTableRowsValues, ({ subFieldValues }) => {
      const areDataFieldValuesPresent =
        _.findIndex(subFieldValues, ({ id }) => id === dataField) > -1;

      _.forEach(subFieldValues, ({ id, values }) => {
        if (_.includes(subFieldIds, id)) {
          const subFieldObj = subFieldObjById[id];
          const fieldUid = _.get(subFieldObj, "config.filteredBy[0]", null);

          const resolvedMinimalTree = options[id];
          let setKey = "nodes";
          if (_.get(subFieldObj, "type", "") === "IBBenchmarks") {
            setKey = "benchmarks";
          }
          const nodes = _.get(resolvedMinimalTree, setKey, []);
          //Only consider those values which are visible in Unit plan
          const filteredValues = _.filter(
            values,
            value => _.findIndex(nodes, ({ id }) => value === id) > -1
          );

          let benchmarkSet = {};
          if (_.get(subFieldObj, "type", "") === "IBBenchmarks") {
            benchmarkSet = getPYPElementSetFromNodes({ nodes, startDepth: 1 });
          }

          const updatedValue = getElementValue({
            fieldObj: subFieldObjById[id],
            valueKeys: filteredValues,
            resolvedValue: options[id],
            subjectIds,
            benchmarkSet,
            fieldUID: fieldUid,
          });

          const subFieldEditModeValues = subFieldValueById[id].editModeValues;
          const subFieldViewModeValues = subFieldValueById[id].viewModeValues;

          /**
           * Rows in which dataFieldValues are not present, subFieldValues will be aggregated across all rows.
           * For rows in which dataFieldValues are present, subFieldValues will be persisted for that row only.
           */
          subFieldValueById[id] = {
            editModeValues: !areDataFieldValuesPresent
              ? [...subFieldEditModeValues, ...filteredValues]
              : filteredValues,
            viewModeValues: !areDataFieldValuesPresent
              ? [...subFieldViewModeValues, ...updatedValue]
              : updatedValue,
          };
        } else {
          const popupFields = [];
          /**
           * To decide which popup fields will be available for current data field:
           * fields which belongs to different windowgroup from current data field, all fields will be considered.
           * For fields that belongs to same windowgroup, only those fields will be considered which are present in current table row and which are not equal to data field
           */
          _.forEach(windowConfigGroupedByWindowGroup, windowConfigs => {
            const fields = _.flatMap(windowConfigs, ({ fields }) => fields);
            if (!_.includes(fields, id)) {
              popupFields.push(...fields);
            } else {
              const currentRowFields = _.map(subFieldValues, ({ id }) => id);
              const fieldsForPopup = _.filter(
                fields,
                fieldKey =>
                  _.includes(currentRowFields, fieldKey) && fieldKey !== id
              );
              popupFields.push(...fieldsForPopup);
            }
          });

          const popupData = _.pick(subFieldValueById, popupFields);
          _.forEach(values, ({ id }) => {
            popupDataById[id] = popupData;
          });
        }
      });
    });

    /**
     * Reset subField values after table is iteration is finished
     */
    subFieldValueById = _.reduce(
      subFields,
      (result, { key }) => {
        return {
          ...result,
          [key]: { viewModeValues: [], editModeValues: [] },
        };
      },
      {}
    );
  });

  return popupDataById;
};

export const getPopupDataForCheckListWithPopupMemoized = _.memoize(
  getPopupDataForCheckListWithPopup,
  params => JSON.stringify(params)
);

const getAssessmentFieldValuesForPopup = ({ fieldValues, subFields }) => {
  return _.reduce(
    subFields,
    (result, field) => {
      const fieldUid = _.get(field, "config.filteredBy[0]", null);
      const value = _.get(
        _.find(fieldValues, ({ uid }) => uid === fieldUid),
        "value",
        null
      );
      return { ...result, [_.get(field, "key")]: value };
    },
    {}
  );
};

export const getAssessmentFieldValuesForPopupMemoized = _.memoize(
  getAssessmentFieldValuesForPopup,
  params => JSON.stringify(params)
);

export const getPrompts = ({ promptsConfig = {} }) => {
  return _.get(promptsConfig, "showPrompts", false)
    ? _.get(promptsConfig, "label", "")
    : null;
};

export const getOrganizationRegionSubjects = ({ subjects, organizationId }) => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);

  const organizationTags = _.get(organizationDetails, "tags", []);
  const regionId = _.get(
    _.first(_.filter(organizationTags, tag => tag.key === "REGION")),
    "id",
    ""
  );

  //1. filter tags which has key of REGION
  //2. if subjects tags includes current region (that means subject is of current region org)
  //or tags are empty that means that are all regions subject
  //then only return that subject
  const orgSubjects = _.reduce(
    subjects,
    (result, subject) => {
      const subjectTags = _.get(subject, "tags", []);
      const tags = _.map(
        _.filter(subjectTags, tag => tag.key === "REGION"),
        tag => tag.id
      );

      if (_.includes(tags, regionId) || _.isEmpty(subjectTags)) {
        result.push({
          label: subject.name,
          value: subject.id,
        });
      }

      return result;
    },
    []
  );

  return orgSubjects;
};

export const getDisableEditUnitFlowResourcesPermValue = () =>
  ACLStore.can("FeatureFlag:DisableEditUnitFlowResources");

export const getDisableDeleteUnitFlowResourcesPermValue = () =>
  ACLStore.can("FeatureFlag:DisableDeleteUnitFlowResources");

export const showUnitCode = ({ unitCodeValue }) => {
  return ACLStore.can("FeatureFlag:ShowUnitCode") && !_.isEmpty(unitCodeValue);
};

const getMYPUnitPlanSubtext = ({
  objectiveLevelNodes,
  subjects,
  t,
  shouldJoinArray = true,
}) => {
  // if we are not restricting objective phase selection, show only subjects
  // the check for objectiveLevelNodes is done as there may be no objectives for the subjects
  if (
    ACLStore.can("FeatureFlag:RemoveMYPObjectivesPhase") ||
    _.isEmpty(objectiveLevelNodes)
  ) {
    const subjectNamesWithArchivedText = _.map(subjects, subject =>
      getArchivedText({
        name: subject?.name,
        t,
        isArchived: _.get(subject, "isArchived", false),
      })
    );
    return shouldJoinArray
      ? _.join(subjectNamesWithArchivedText, ", ")
      : subjectNamesWithArchivedText;
  }

  // In myp if we import other subjects then they are not mapped to any objectiveNode so we have to push them seperately
  const unMappedSubjects = [];
  const subjectsWithCriteria = _.reduce(
    subjects,
    (list, subject) => {
      const nodes = _.filter(objectiveLevelNodes, objectiveNode => {
        return _.some(_.get(objectiveNode, "associatedParents", []), parent => {
          return (
            _.isEqual(parent?.id, subject?.id) &&
            _.isEqual(parent?.__typename, "Subject")
          );
        });
      });

      const { name, isArchived } = subject;
      const subjectLabel = getArchivedText({ name, isArchived, t });
      if (!_.isEmpty(nodes)) {
        _.forEach(nodes, node => {
          list.push(_.join([node?.label, subjectLabel], " - "));
        });
      } else {
        unMappedSubjects.push(subjectLabel);
      }

      return list;
    },
    []
  );

  let unitPlanSubtext = shouldJoinArray
    ? _.join(subjectsWithCriteria, " | ")
    : subjectsWithCriteria;
  if (!_.isEmpty(unMappedSubjects)) {
    unitPlanSubtext = shouldJoinArray
      ? `${unitPlanSubtext}, ${_.join(unMappedSubjects, ", ")}`
      : _.concat(unitPlanSubtext, unMappedSubjects);
  }

  return unitPlanSubtext;
};

export const getMYPUnitPlanSubtextMemoized = _.memoize(
  params => getMYPUnitPlanSubtext(params),
  props => JSON.stringify(props)
);

const getCourseOverviewLabel = ({ curriculumType }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_DP:
      return "common:course_overview";

    case CURRICULUM_TYPE_MYP:
      return "common:subject_view";

    default:
      return "common:table_view";
  }
};

export const getCourseOverviewLabelMemoized = _.memoize(
  params => getCourseOverviewLabel(params),
  props => JSON.stringify(props)
);

const _generateTemplateFieldQueryVariables = ({
  templateIds,
  locale,
  curriculumProgramId,
}) => {
  const templateConfigLevels = _.map(templateIds, templateId => ({
    id: templateId,
    type: "TEMPLATE",
  }));

  return {
    templateIds,
    templateFieldConfigFilter: {
      configLevel: [{ type: "DEFAULT" }, ...templateConfigLevels],
    },
    fieldPropertyValuesFilters: {
      curriculumProgramId,
      configLevel: [{ type: "DEFAULT" }, ...templateConfigLevels],
      ...(ACLStore.can(ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE)
        ? { locale }
        : {}),
    },
  };
};

export const generateTemplateFieldQueryVariables = _.memoize(
  _generateTemplateFieldQueryVariables,
  params => JSON.stringify(params)
);

export const getPropertyValueFromConfigLevels = ({ values, templateId }) => {
  const templateSpecificValue = _.get(
    _.find(values, ({ configLevel: { id, type } }) => {
      return id === templateId && type === "TEMPLATE";
    }),
    "value"
  );

  if (_.isUndefined(templateSpecificValue)) {
    return _.get(
      _.find(values, ({ configLevel: { type } }) => {
        return type === "DEFAULT";
      }),
      "value"
    );
  }

  return templateSpecificValue;
};

const _convertPropertyToConfig = ({ property, properties, templateId }) => {
  const { id, globalFieldProperty, values, children } = property;

  const propertyName = _.get(globalFieldProperty, ["property"]);

  const config = {
    [propertyName]: {
      id,
      value: getPropertyValueFromConfigLevels({ values, templateId }),
    },
  };

  if (!_.isEmpty(children)) {
    const childProperties = _.filter(properties, ({ id }) =>
      _.includes(children, id)
    );

    const childConfig = _.reduce(
      childProperties,
      (result, property) => {
        return {
          ...result,
          ..._convertPropertyToConfig({ property, properties, templateId }),
        };
      },
      {}
    );

    return {
      ...config,
      [propertyName]: { ...config[propertyName], ...childConfig },
    };
  }

  return config;
};

const _convertFieldPropertiesToFieldConfig = ({ properties, templateId }) => {
  const propertiesWithNoParent = _.filter(
    properties,
    ({ parentId }) => !parentId
  );

  const config = _.reduce(
    propertiesWithNoParent,
    (result, property) => {
      return {
        ...result,
        ..._convertPropertyToConfig({ property, properties, templateId }),
      };
    },
    {}
  );

  return config;
};

const _generateFieldListFromTemplateFields = ({
  templateFields,
  templateId,
}) => {
  return _.reduce(
    templateFields,
    (result, { node }) => {
      const { id, uid, config } = node;
      const fieldConfig = _convertFieldPropertiesToFieldConfig({
        properties: config,
        templateId,
      });

      fieldConfig.id = id;
      return { ...result, [uid]: fieldConfig };
    },
    {}
  );
};

export const generateFieldListFromTemplateFields = _.memoize(
  _generateFieldListFromTemplateFields,
  params => JSON.stringify(params)
);

export const getFilterTagsMemoize = _.memoize(
  params => getFilterTags(params),
  params => JSON.stringify(params)
);

export const getUnitPlanFieldResponseFilter = () => {
  const userInfo = getUserInfo();
  const userType = _.get(userInfo, "user_type");

  let responseFilter;

  if (userType === "student") {
    responseFilter = {
      studentIds: [_.get(userInfo, "id")],
    };
  }

  if (userType === "parent") {
    responseFilter = {
      studentIds: [_.get(userInfo, "childID")],
    };
  }

  return responseFilter;
};

const getFilterTags = ({ tags = [] }) => {
  return _.reduce(
    tags,
    (result, tag) => {
      if (tag.key == "REGION") {
        result.push(tag.id);
      }
      return result;
    },
    []
  );
};

const getPlannerElementSetFilters = ({
  selectedSubjectLevels,
  plannerElementTypes,
  subjects,
}) => {
  const plannerFilters = {};
  if (_.includes(plannerElementTypes, ELEMENT_TYPE_MYP_OBJECTIVES)) {
    plannerFilters.upToDepth = 1;
  }
  if (
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_AIM) ||
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_OBJECTIVES)
  ) {
    plannerFilters.genericTags = selectedSubjectLevels;
    plannerFilters.associatedParents = [
      {
        ids: _.get(subjects, "value", []),
        type: "SUBJECT",
      },
    ];
  }
  if (
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_CP_PPS_LEARNING_OUTCOMES)
  ) {
    plannerFilters.associatedParents = [
      {
        ids: _.get(subjects, "value", []),
        type: "SUBJECT",
      },
    ];
  }

  return plannerFilters;
};

export const getPlannerElementSetFiltersMemoized = _.memoize(
  params => getPlannerElementSetFilters(params),
  props => JSON.stringify(props)
);

const getPlannerElementNodeFilters = ({
  selectedSubjectLevels,
  plannerElementTypes,
  subjects,
}) => {
  const plannerElementNodeFilters = {};
  if (_.includes(plannerElementTypes, ELEMENT_TYPE_MYP_OBJECTIVES)) {
    plannerElementNodeFilters.depth = 1;
  }
  if (
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_AIM) ||
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_OBJECTIVES)
  ) {
    plannerElementNodeFilters.tags = selectedSubjectLevels;
    plannerElementNodeFilters.subjects = _.get(subjects, "value", []);
  }
  if (
    _.includes(plannerElementTypes, ELEMENT_TYPE_DP_CP_PPS_LEARNING_OUTCOMES)
  ) {
    plannerElementNodeFilters.subjects = _.get(subjects, "value", []);
  }

  return plannerElementNodeFilters;
};

export const getPlannerElementNodeFiltersMemoized = _.memoize(
  params => getPlannerElementNodeFilters(params),
  props => JSON.stringify(props)
);

const getSubjectGroupListOptions = ({ subjectGroups }) => {
  return _.map(subjectGroups, subjectGroup => ({
    label: subjectGroup.name,
    value: subjectGroup.id,
  }));
};

export const getSubjectGroupListOptionsMemoized = _.memoize(
  getSubjectGroupListOptions,
  JSON.stringify
);

const _getTemplateDropdownOptions = ({ templates, t, unitType }) => {
  const templateOptions = _.sortBy(
    _.map(templates, template => {
      const templateName = _.get(template, "name", "");
      return {
        key: `${unitType}:${template.id}`,
        label: _.get(template, "isBilingual", false)
          ? `${templateName} (${t("common:bilingual")})`
          : templateName,
        isLabelLocalized: true,
      };
    }),
    [
      template => {
        return _.trim(_.lowerCase(_.get(template, "label", "")));
      },
    ]
  );

  return [
    {
      isItemGroup: true,
      label: t("common:select_a_with_label", {
        label: t("common:template"),
      }),
      key: "select-template",
      isLabelLocalized: true,
      options: templateOptions,
    },
  ];
};

const _getCreateUnitDropdownConfig = ({
  unitTypeOptions,
  plannerTemplates,
  mapleBearLikeOrg,
  isEducator,
  isDemo,
  isImportDisabled,
  curriculumType,
  globalGradeUid,
  t,
  baseCurriculum,
  hideImportOptions,
  isCourseBasedGradingEnabled,
  isLearningCoursesEnabled,
  learningCourseSubjects,
}) => {
  // we are using different locales for create
  const unitTypeOptionsWithUnitLocale = _.map(unitTypeOptions, option => {
    const unitType = option.key;
    const localeWithUnitSuffix = UNIT_TYPE_WITH_UNIT_SUFFIX_LOCALES[unitType];

    const label = t(localeWithUnitSuffix);
    return {
      ...option,
      label,
      isLabelLocalized: true,
    };
  });
  let templateSize = "";
  const options = _.filter(
    _.map(unitTypeOptionsWithUnitLocale, opt => {
      const templates = _.filter(
        plannerTemplates,
        template => template.unitType === opt.value
      );

      const unitType = opt.value;

      const options = _getTemplateDropdownOptions({
        templates,
        t,
        unitType,
      });
      templateSize = _.size(templates);
      // if only a single template is associated with a unit type,
      // then clicking the unit type should take the user to unit
      // creation page. hence we will put key = template.id in this case
      if (_.size(templates) === 1) {
        return {
          ...opt,
          key: `${unitType}:${_.get(_.first(templates), "id", "")}`,
          isLabelLocalized: true,
        };
      } else {
        // don't give option for a unitType if no templates
        // of that type are mapped to the grade
        if (!_.isEmpty(templates)) {
          return {
            ...opt,
            key: opt.value,
            isLabelLocalized: true,
            isSubMenu: _.size(templates) > 1,
            options: options,
          };
        }
      }
    }),
    option => !_.isUndefined(option)
  );

  const optionsSize = _.size(options);
  let createUnitDropDownOptions = [];
  let enableMultilineOptions = false;

  // if course based grading is enabled and only one template is associated
  // filter out transdisciplinary inquiry template
  // NOTE: only for non-learning-courses (TopHeader/TopHeaderV2). Learning Courses
  // uses the isLearningCoursesEnabled general case below (flat list for all templates)
  if (
    !isLearningCoursesEnabled &&
    isCourseBasedGradingEnabled &&
    templateSize == 1
  ) {
    const filteredOptions = _.filter(
      options,
      item => item.value !== "ubdInquiry"
    );

    const unitTypeDropdownOptions = _.first(filteredOptions);
    const [unitType, templateId] = _.split(unitTypeDropdownOptions.key, ":");

    createUnitDropDownOptions.push({
      isItemGroup: false,
      label: t("course:create_unit"),
      isLabelLocalized: true,
      key: `${unitType}:${templateId}`,
      icon: <UnitPlanOutlined />,
    });
    return { createUnitDropDownOptions, enableMultilineOptions, optionsSize };
  }

  // Pre-sort templates alphabetically for all Learning Courses paths
  const sortedPlannerTemplates = isLearningCoursesEnabled
    ? _.sortBy(plannerTemplates, template => _.trim(_.lowerCase(template.name)))
    : plannerTemplates;

  // if curriculum type is MYP only
  // filter out templates based on number of learningCourseSubjects
  if (isLearningCoursesEnabled && curriculumType === CURRICULUM_TYPE_MYP) {
    // Determine which unit type to show based on subject count:
    // 1 subject → subject-specific, 2+ subjects → interdisciplinary
    const filteredUnitType =
      _.size(learningCourseSubjects) === 1
        ? "ibMYPSubjectSpecific"
        : "ibMYPInterdisciplinary";

    const filteredTemplates = _.filter(
      sortedPlannerTemplates,
      template => template.unitType === filteredUnitType
    );

    if (_.isEmpty(filteredTemplates)) {
      createUnitDropDownOptions = [
        {
          key: "no-templates-mapped",
          label: t("unitPlan:no_unit_templates_mapped"),
          disabled: true,
          subText: t("unitPlan:no_unit_templates_mapped_subtext"),
          isLabelLocalized: true,
        },
      ];
    } else {
      createUnitDropDownOptions = _.map(filteredTemplates, template => ({
        key: `${filteredUnitType}:${template.id}`,
        label: _.get(template, "isBilingual", false)
          ? `${template.name} (${t("common:bilingual")})`
          : template.name,
        isLabelLocalized: true,
      }));
    }

    return { createUnitDropDownOptions, enableMultilineOptions, optionsSize };
  }

  if (isLearningCoursesEnabled) {
    // New flat-list behavior for Learning Courses (non-MYP curricula: DP, PYP, UBD)
    if (!optionsSize) {
      // No templates mapped for any unit type
      createUnitDropDownOptions = [
        {
          key: "no-templates-mapped",
          label: t("unitPlan:no_unit_templates_mapped"),
          disabled: true,
          subText: t("unitPlan:no_unit_templates_mapped_subtext"),
          isLabelLocalized: true,
        },
      ];
      enableMultilineOptions = true;
    } else if (isCourseBasedGradingEnabled || optionsSize === 1) {
      // CBG: all templates from all unit types shown flat
      // Single unit type: all templates for that type shown flat
      createUnitDropDownOptions = _.flatMap(options, opt => {
        const unitType = opt.value;
        const unitTemplates = _.filter(
          sortedPlannerTemplates,
          template => template.unitType === unitType
        );
        return _.map(unitTemplates, template => ({
          key: `${unitType}:${template.id}`,
          label: _.get(template, "isBilingual", false)
            ? `${template.name} (${t("common:bilingual")})`
            : template.name,
          isLabelLocalized: true,
        }));
      });
    } else {
      // Multiple unit types (non-CBG): each unit type shown as isItemGroup section header
      _.forEach(options, opt => {
        const unitType = opt.value;
        const unitTemplates = _.filter(
          sortedPlannerTemplates,
          template => template.unitType === unitType
        );
        const templateItems = _.map(unitTemplates, template => ({
          key: `${unitType}:${template.id}`,
          label: _.get(template, "isBilingual", false)
            ? `${template.name} (${t("common:bilingual")})`
            : template.name,
          isLabelLocalized: true,
        }));

        createUnitDropDownOptions.push({
          isItemGroup: true,
          label: opt.label,
          isLabelLocalized: opt.isLabelLocalized,
          options: templateItems,
        });
      });
    }
  } else if (optionsSize == 1) {
    // Old behavior for non-learning-courses (TopHeader/TopHeaderV2)
    const unitTypeDropdownOptions = _.first(options);
    if (_.get(unitTypeDropdownOptions, "isSubMenu", false)) {
      createUnitDropDownOptions = _.get(unitTypeDropdownOptions, "options", []);
    } else {
      const [unitType, templateId] = _.split(unitTypeDropdownOptions.key, ":");

      const template = _.find(
        plannerTemplates,
        template => template.id === templateId
      );
      createUnitDropDownOptions.push(
        ..._getTemplateDropdownOptions({
          templates: [template],
          t,
          unitType,
        })
      );
    }
  } else if (!optionsSize) {
    createUnitDropDownOptions.push({
      isItemGroup: true,
      key: "create_unit",
      label: t("common:create_unit"),
      isLabelLocalized: true,
      options: [
        {
          key: "no-templates-mapped",
          label: t("unitPlan:no_unit_templates_mapped"),
          disabled: true,
          subText: t("unitPlan:no_unit_templates_mapped_subtext"),
          isLabelLocalized: true,
        },
      ],
    });
    enableMultilineOptions = true;
  } else {
    createUnitDropDownOptions.push({
      isItemGroup: true,
      key: "create_unit",
      label: (
        <div className={classes.labelStyle}>{t("common:create_unit")}</div>
      ),
      isLabelLocalized: true,
      options,
    });
  }

  if (!hideImportOptions) {
    if (isEducator && !isDemo) {
      createUnitDropDownOptions.push({
        label: `${t("common:import_from")} MB ${t("common:library")}`,
        value: "import_from_repo",
        key: "import_from_repo",
      });
    }
    if (baseCurriculum) {
      createUnitDropDownOptions.push({
        label: t("unitPlan:import_unit_with_curriculum", {
          label: baseCurriculum.label,
        }),
        value: "import_from_base_library",
        key: "import_from_base_library",
      });
    }
    if (!mapleBearLikeOrg) {
      createUnitDropDownOptions.push({
        label: t("unitPlan:import_unit_from_library"),
        value: "import_from_unit_library",
        key: "import_from_unit_library",
        disabled: isImportDisabled,
        disabledTooltip: t("unitPlan:map_subject_to_enable_import_units"),
      });
    }
    if (canImportUnitFromToddleLibrary({ curriculumType, globalGradeUid })) {
      createUnitDropDownOptions.push({
        label: t("toddleLibrary:import_from_toddle_library"),
        value: "import_from_toddle_library",
        key: "import_from_toddle_library",
      });
    }
  }
  return { createUnitDropDownOptions, enableMultilineOptions, optionsSize };
};

export const getCreateUnitDropdownConfig = getMemoizedLangFunction(
  _getCreateUnitDropdownConfig
);

const All_CURRICULUM_PROGRAMS = [
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_UBD,
];
// Define field options (reusable and centralized)
export const FIELD_OPTIONS = {
  subjects: {
    key: "subjects",
    label: "common:SUBJECT",
    type: "checkboxlist",
    permission: null,
  },
  unit_hours: {
    key: "unit_hours",
    label: "unitPlan:unit_hours",
    type: "checkboxlist",
    permission: null,
  },
  duration_week: {
    key: "duration_week",
    label: "unitPlan:duration_week",
    type: "checkboxlist",
    permission: null,
  },
  duration_month: {
    key: "duration_month",
    label: "common:duration",
    type: "checkboxlist",
    permission: null,
  },
  level: {
    key: "level",
    label: "unitPlan:level",
    type: "checkboxlist",
    permission: CORE_AND_OTHER_TYPES,
  },
  learning_experiences: {
    key: "learning_experiences",
    label: "unitPlan:learning_experiences",
    type: "checkboxlist",
    permission: null,
  },
  assessments: {
    key: "assessments",
    label: "common:assessment_label_plural",
    type: "checkboxlist",
    permission: "FeatureFlag:EnablePYPAssessment",
  },
  collaborators: {
    key: "collaborators",
    label: "common:collaborators",
    type: "checkboxlist",
    permission: null,
    visibilityCheckFn: ({ isBlueprintCourse }) => {
      return !isBlueprintCourse;
    },
  },
  shared_with_students_family: {
    key: "shared_with_students_family",
    label: "unitPlan:shared_with_students_family",
    type: "checkboxlist",
    permission: null,
    visibilityCheckFn: ({ isBlueprintCourse }) => {
      return !isBlueprintCourse;
    },
  },
  show_blueprint_lock: {
    key: "show_blueprint_lock",
    label: "course:show_blueprint_lock_indicator",
    type: "checkboxlist",
    permission: null,
    visibilityCheckFn: ({
      isLearningCoursesEnabled,
      isBlueprintCourse,
      isTeacherCourseAssociationEnabled,
      isCourseAssociatedWithBlueprint,
    }) => {
      return (
        isLearningCoursesEnabled &&
        !isBlueprintCourse &&
        isTeacherCourseAssociationEnabled &&
        isCourseAssociatedWithBlueprint
      );
    },
  },
  unit_code: {
    key: "unit_code",
    label: "unitPlan:unit_code",
    type: "checkboxlist",
    isMapleBearOnly: true,
  },
  owner: {
    key: "owner",
    label: "common:owner",
    type: "checkboxlist",
    permission: "FeatureFlag:ChangeUnitOwner",
  },
};

// Define curriculum-specific order for display fields
export const DISPLAY_SETTING_CURRICULUM_FIELDS = {
  [CURRICULUM_TYPE_DP]: [
    "duration_month",
    "level",
    "duration_week",
    "unit_hours",
    "learning_experiences",
    "assessments",
    "collaborators",
    "shared_with_students_family",
    "show_blueprint_lock",
    "owner",
  ],
  [CURRICULUM_TYPE_MYP]: [
    "subjects",
    "unit_hours",
    "duration_week",
    "learning_experiences",
    "assessments",
    "collaborators",
    "shared_with_students_family",
    "show_blueprint_lock",
    "owner",
  ],
  [CURRICULUM_TYPE_PYP]: [
    "subjects",
    "duration_week",
    "learning_experiences",
    "assessments",
    "collaborators",
    "shared_with_students_family",
    "show_blueprint_lock",
    "owner",
  ],
  [CURRICULUM_TYPE_UBD]: [
    "subjects",
    "duration_week",
    "learning_experiences",
    "assessments",
    "collaborators",
    "shared_with_students_family",
    "show_blueprint_lock",
    "owner",
    "unit_code",
  ],
};

// Generate display settings based on curriculum
const getDisplaySettings = ({
  t,
  curriculumType,
  isLearningCoursesEnabled,
  subjectGroupType,
  isTeacherCourseAssociationEnabled,
  isCourseAssociatedWithBlueprint,
}) => {
  const state = store?.getState();
  const selectedLearningCourse = getSelectedLearningCourse(state);
  const isBlueprintCourse = getIsBlueprintCourse(selectedLearningCourse);

  const isPYP = _.isEqual(curriculumType, CURRICULUM_TYPE_PYP);
  const isDP = _.isEqual(curriculumType, CURRICULUM_TYPE_DP);

  return Object.entries(DISPLAY_SETTING_CURRICULUM_FIELDS).map(
    ([curriculum, fields]) => ({
      key: "display",
      label: t("common:display"),
      isItemGroup: true,
      hasDivider: false,
      curriculum: [curriculum],
      options: fields
        .map(field => {
          const fieldOption = FIELD_OPTIONS[field];

          let label = t(fieldOption.label);
          if (field === "subjects") {
            if (curriculumType === CURRICULUM_TYPE_PYP)
              label = t("unitPlan:subjects_theme_label");
            if (curriculumType === CURRICULUM_TYPE_MYP)
              label = t("unitPlan:MYP_subject_label");
          }

          // classes display setting used in learning courses
          // Conditionally only updating label and keeping the key same as it will create issue fetching existing configurations
          // also to enable classes display setting in learning courses we will need to delete the existing configuration

          if (field === "collaborators") {
            label = isLearningCoursesEnabled
              ? t("common:class_plural")
              : t("common:collaborators");
          }

          return { ...fieldOption, label };
        })
        .filter(fieldOption => {
          const {
            key,
            visibilityCheckFn,
            permission,
            isMapleBearOnly,
          } = fieldOption;

          let isVisible = true;
          if (isLearningCoursesEnabled && key === "subjects") {
            isVisible = !getIsCourseBasedGradingEnabled();
          } else if (_.isNull(permission && !isMapleBearOnly)) {
            isVisible = true;
          } else if (isPYP && key === "assessments") {
            isVisible = ACLStore.can(permission);
          } else if (key === "owner") {
            isVisible = isLearningCoursesEnabled && ACLStore.can(permission);
          } else if (isDP) {
            isVisible = !_.includes(permission, subjectGroupType);
          } else if (isMapleBearOnly) {
            isVisible = mapleBearLikeOrg();
          }

          // If visibilityCheckFn exists, use it to determine visibility
          if (visibilityCheckFn) {
            isVisible =
              isVisible &&
              visibilityCheckFn({
                isLearningCoursesEnabled,
                isBlueprintCourse,
                isTeacherCourseAssociationEnabled,
                isCourseAssociatedWithBlueprint,
              });
          }
          // If no visibilityCheckFn, include the field option
          return isVisible;
        }),
    })
  );
};

// Combine all settings with additional groups
export const getSettingsMenu = ({
  t,
  curriculumType,
  subjectGroupType,
  isTeacherCourseAssociationEnabled = false,
  isCourseAssociatedWithBlueprint = false,
}) => {
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const isCourseBasedGradingEnabled = getIsCourseBasedGradingEnabled();
  const displaySettings = getDisplaySettings({
    t,
    curriculumType,
    isLearningCoursesEnabled,
    subjectGroupType,
    isTeacherCourseAssociationEnabled,
    isCourseAssociatedWithBlueprint,
  });

  const menuOptions = [
    {
      key: "group_by",
      label: t("common:view"),
      isItemGroup: true,
      hasDivider: true,
      curriculum: All_CURRICULUM_PROGRAMS.filter(
        program => program !== "IB_DP"
      ),
      shouldHide: isCourseBasedGradingEnabled,
      options: [
        {
          key: "group_by_unit_type",
          label: t("unitPlan:group_by_unit_type"),
          type: "checkbox",
          permission: null,
        },
      ],
    },
    {
      key: "sort_unit_by",
      label: t("unitPlan:sort_unit_by"),
      isItemGroup: true,
      hasDivider: true,
      curriculum: All_CURRICULUM_PROGRAMS,
      options: [
        {
          key: "TITLE",
          label: t("common:name"),
          type: "radio",
          permission: null,
        },
        {
          key: "START_DATE",
          label: t("common:start_date"),
          type: "radio",
          permission: null,
        },
        {
          key: "UPDATED_AT",
          label: t("common:last_edited_label"),
          type: "radio",
          permission: null,
        },
        {
          key: "CUSTOM",
          label: t("common:custom"),
          type: "radio",
          icon: <SettingsOutlined />,
          permission: null,
        },
      ],
    },
    ...displaySettings,
  ];

  return _.filter(
    menuOptions,
    setting =>
      setting?.curriculum?.includes(curriculumType) && !setting?.shouldHide
  );
};

export const getDisplaySetting = displaySettings => {
  const sortBy = _.get(displaySettings, "sort", {});
  const displayBy = _.get(displaySettings, "display", {});
  const isCourseBasedGradingEnabled = getIsCourseBasedGradingEnabled();
  const isGroupByUnitType = isCourseBasedGradingEnabled
    ? false
    : _.get(displaySettings, "group_by_unit_type", false);
  const sortUnitBy = _.findKey(sortBy, value => value) ?? "TITLE";
  const {
    subjects: showSubjects,
    unit_hours: showUnitHours,
    duration_month: showMonthDuration,
    level: showLevels,
    duration_week: showWeeksDuration,
    learning_experiences: showLE,
    assessments: showAssessments,
    collaborators: showCollaborators,
    shared_with_students_family: showSharedWithStudentsFamily,
    show_blueprint_lock: showBlueprintLock,
    unit_code: showUnitCode,
    owner: showOwner,
  } = displayBy;

  return {
    sortUnitBy,
    isGroupByUnitType,
    showSubjects,
    showUnitHours,
    showMonthDuration,
    showLE,
    showAssessments,
    showCollaborators,
    showSharedWithStudentsFamily,
    showBlueprintLock,
    showLevels,
    showWeeksDuration,
    showUnitCode,
    showOwner,
  };
};

const getDefaultDisplaySetting = () => {
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  return {
    group_by_unit_type: isLearningCoursesEnabled ? false : true,
    sort: {
      TITLE: false,
      UPDATED_AT: false,
      START_DATE: true,
      CUSTOM: false,
    },
    display: {
      subjects: true,
      unit_hours: true,
      duration_month: true,
      level: true,
      duration_week: true,
      learning_experiences: true,
      assessments: true,
      collaborators: true,
      shared_with_students_family: true,
      ...(isLearningCoursesEnabled
        ? { show_blueprint_lock: true, owner: true }
        : {}),
      unit_code: true,
    },
  };
};

export const getDisplayConfigurationSetting = ({
  state,
  userId,
  curriculumProgramId,
}) => {
  const isCoursePlanRouteEnabled = getIsCoursePlanRouteEnabled();
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  if (isCoursePlanRouteEnabled && isLearningCoursesEnabled) {
    return _.get(state, "unitPlans.unitOrderingDisplaySetting", {});
  }

  const existingSettings = getSettingValue({
    name: "UnitFeedOrdering",
    userId,
    currentCurriculumProgramId: curriculumProgramId,
  });

  const defaultDisplaySettings = getDefaultDisplaySetting();
  let displaySettings = defaultDisplaySettings;

  if (!_.isEmpty(existingSettings)) {
    displaySettings = _.merge({}, defaultDisplaySettings, existingSettings);
  }

  return displaySettings;
};

export const getPlannerTemplatesForCreateDropdown = ({
  queryData,
  selectedSubjects,
  curriculumType,
}) => {
  const gradeSubjects = _.unionBy(_.get(queryData, "[0].subjects", []), "id");
  const subjectType = _.get(
    _.find(gradeSubjects, sub => _.includes(selectedSubjects, sub.id)),
    "type",
    SUBJECT_TYPE_GENERIC
  );
  // get unique unit plan planner templates for the grade
  let plannerTemplates = _.filter(
    _.reduce(
      queryData,
      (result, grade) => {
        result = _.unionBy(result, grade["plannerTemplates"], "id");
        return result;
      },
      []
    ),
    template => _.get(template, "type", "") === "UNIT_PLAN"
  );
  if (curriculumType === CURRICULUM_TYPE_DP) {
    plannerTemplates = _.filter(plannerTemplates, template => {
      if (subjectType != SUBJECT_TYPE_GENERIC)
        return template.subType == subjectType;
      return _.isEmpty(template.subType);
    });
  }
  return plannerTemplates;
};

export const UNIT_TYPE_TAGS_INFO = {
  [PYP_TRANSDISCIPLINARY]: {
    color: "violet",
    labelLocale: "unitPlan:transdisciplinary_acronym",
  },
  [UBD_INQUIRY]: {
    color: "yellow",
    labelLocale: "unitPlan:interdisciplinary_unit_acronym",
  },
  [MYP_INTERDISCIPLINARY]: {
    color: "yellow",
    labelLocale: "unitPlan:interdisciplinary_unit_acronym",
  },
};

// not memoizing, causes problem with localisation
export const getUnitTypeTag = ({ unitType, t }) => {
  if (!_.has(UNIT_TYPE_TAGS_INFO, unitType)) return null;

  const unitTypeTagInfo = UNIT_TYPE_TAGS_INFO[unitType];

  return (
    <Tag color={unitTypeTagInfo?.color} size="xx-small">
      {unitTypeTagInfo?.labelLocale ? t(unitTypeTagInfo.labelLocale) : ""}
    </Tag>
  );
};

export const getUnitPlansFieldUIDs = ({
  currentViewType,
  curriculumType,
  state,
}) => {
  let config;
  switch (currentViewType) {
    case CARD:
    case CARD_VIEW:
      config = {
        fieldUIDs: ["hours", "subjectLevel", "objectiveLevel"],
      };
      break;

    case TIMELINE:
    case TIMELINE_VIEW:
      config = {
        fieldUIDs: FIELD_UID_CURRICULUM_MAPPING[curriculumType],
      };
      break;

    case TABLE:
    case COURSE_OVERVIEW:
      config = {
        fieldUIDs: _.get(state, "courseOverview.filters.fieldUIDs", []),
      };
      break;

    default:
      config = {};
  }

  const fieldUIDs = _.get(config, "fieldUIDs", {});

  return fieldUIDs;
};

// this function returns plain unit plan subtitle text for timeline and course overview
// without week and hours count
const getUnitPlanSubtitleText = ({
  curriculumType,
  unitType,
  fields,
  startDate,
  endDate,
  t,
}) => {
  /*
    Subtexts:
      PYP:  <theme or subject depending on unit type>
      MYP:  <Subject / subjects with criteria>
      UbD:  <Subject / subjects>
      DP:   <startdate> - <enddate>
  */
  const subjects = _.get(
    _.find(fields, { uid: "subjects" }),
    "resolvedMinimalTree.subjects"
  );

  const subjectNames = _.join(
    _.map(subjects, subject => {
      return getArchivedText({
        name: _.get(subject, "name", ""),
        isArchived: _.get(subject, "isArchived", false),
        t,
      });
    }),
    ", "
  );

  switch (curriculumType) {
    case CURRICULUM_TYPE_PYP: {
      if (_.isEqual(unitType?.value, "ibStandAlone")) {
        return subjectNames;
      } else {
        const themeLabel = _.get(
          _.find(fields, { uid: "theme" }),
          "resolvedMinimalTree.themes[0].label"
        );

        return themeLabel ?? subjectNames;
      }
    }
    case CURRICULUM_TYPE_MYP: {
      const objectiveLevelNodes = _.get(
        _.find(fields, { uid: "objectiveLevel" }),
        "resolvedMinimalTree.nodes",
        []
      );
      const subjectsWithCriteria = getMYPUnitPlanSubtextMemoized({
        objectiveLevelNodes,
        subjects,
        t,
      });
      return subjectsWithCriteria;
    }
    case CURRICULUM_TYPE_UBD: {
      return subjectNames;
    }
    case CURRICULUM_TYPE_DP: {
      const startingDate = formatDate({ date: startDate, format: "D MMM" });
      const endingDate = formatDate({ date: endDate, format: "D MMM" });

      return `${startingDate} - ${endingDate}`;
    }
  }
};

export const getUnitPlanSubtitleTextMemoized = _.memoize(
  getUnitPlanSubtitleText,
  JSON.stringify
);

export const isSubjectSpecificUnit = ({ unitType }) => {
  const subjectSpecificTypes = [
    PYP_SUBJECT_SPECIFIC,
    UBD_SUBJECT_SPECIFIC,
    MYP_SUBJECT_SPECIFIC,
    UNIT_TYPE_DP,
  ];
  return _.includes(subjectSpecificTypes, unitType);
};

export const getIsVideoSupported = () => {
  return (
    ACLStore.can(TODDLE_PLAY_PERMISSION) ||
    ACLStore.can(UNIT_PLAN_MORE_DETAILS_IN_BANNER_PERMISSION)
  );
};

// Memoized functions to prevent rerending
// -------------------------------------------------------------------------------

const getCentralRepoConfig = ({
  regionId,
  isEducator,
  organizationTags,
  centralRepoCourseId,
}) => {
  return { regionId, isEducator, organizationTags, centralRepoCourseId };
};

export const getCentralRepoConfigMemoized = _.memoize(
  getCentralRepoConfig,
  JSON.stringify
);

const getCourseAcademicYearsIds = ({ courseAcademicYears }) => {
  return _.map(courseAcademicYears, ({ id }) => id);
};

export const getCourseAcademicYearsIdsMemoized = _.memoize(
  getCourseAcademicYearsIds,
  JSON.stringify
);

const getCourseSubjectsForUnitCreation = ({
  courseSubjects,
  curriculumType,
}) => {
  return curriculumType != CURRICULUM_TYPE_UBD || _.size(courseSubjects) == 1
    ? _.map(courseSubjects, subject => subject.id)
    : [];
};

export const getCourseSubjectsForUnitCreationMemoized = _.memoize(
  getCourseSubjectsForUnitCreation,
  JSON.stringify
);

export const getUserCoursesFilters = _.memoize(
  ({
    organizationCurriculumPrograms,
    currentCurriculumProgramId,
    archivalState = "ALL",
  }) => {
    const curriculumProgram = _.find(organizationCurriculumPrograms, {
      id: currentCurriculumProgramId,
    });
    const curriculumProgramIds = _.filter(
      curriculumProgram ? [curriculumProgram.id] : [],
      Boolean
    );
    if (_.size(curriculumProgramIds) === 0) {
      Sentry.addBreadcrumb({
        category: "console",
        level: "info",
        message: JSON.stringify({ curriculumProgram }, null, 4),
      });
    }
    return {
      curriculumProgramIds,
      archivalState,
    };
  },
  params => JSON.stringify(params)
);

const getActiveCourses = ({ userCoursesData }) => {
  return _.filter(_.get(userCoursesData, "courses", []), {
    isArchived: false,
  });
};

export const getActiveCoursesMemoized = _.memoize(
  params => getActiveCourses(params),
  params => JSON.stringify(params)
);

const getCentralRepoConfigData = ({
  isEducator,
  centralRepoCourseId,
  courseId,
}) => {
  const isCentralRepoCourse =
    !_.isEmpty(centralRepoCourseId) && courseId === centralRepoCourseId;

  return {
    isEducator,
    isCentralRepoCourse,
  };
};

export const getCentralRepoConfigDataMemoized = _.memoize(
  params => getCentralRepoConfigData(params),
  params => JSON.stringify(params)
);

const getUnitPlanCoursesSubjects = ({ unitPlanCourses }) => {
  return _.reduce(
    unitPlanCourses,
    (result, course) => {
      result = _.uniqBy([...result, ..._.get(course, "subjects", [])], "id");

      return result;
    },
    []
  );
};

export const getUnitPlanCoursesSubjectsMemoized = _.memoize(
  getUnitPlanCoursesSubjects,
  JSON.stringify
);

export const isReflectionCreatedAtEditEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableReflectionCreatedAtEdit");
};

const getAcademicYear = ({
  isAdminTab,
  adminAcademicYear,
  currentAcademicYear,
  unitPlanAcademicYears,
  unitPlanDetails,
}) => {
  const platformAcademicYearInUnit = _.filter(
    unitPlanAcademicYears,
    year => year.id === currentAcademicYear
  );

  if (isAdminTab && adminAcademicYear) {
    return adminAcademicYear;
  } else if (_.size(unitPlanAcademicYears) > 0) {
    if (_.size(platformAcademicYearInUnit) > 0) {
      return _.get(platformAcademicYearInUnit[0], "id");
    } else {
      return _.get(unitPlanDetails, "academicYears[0].id", "");
    }
  } else {
    return currentAcademicYear;
  }
};

export const hasEditAccess = () => {
  const isRnPEnabled = ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION);
  const isEditPermissionEnabled = ACLStorage.getPermissionValue(
    UNIT_PLAN_EDIT_PERMISSION
  );
  const canEditPlanner = isRnPEnabled ? isEditPermissionEnabled : true;
  return canEditPlanner;
};

export const getAcademicYearMemoized = _.memoize(
  params => getAcademicYear(params),
  params => JSON.stringify(params)
);

export const getLearningCourseGrades = state => {
  const academicYearId = getSelectedAcademicYearId(state);
  const learningCourseId = getSelectedLearningCourseId(state);
  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId,
    academicYearId,
  });
  const learningCourseDetails = getLearningCourseDetailsFromCache(
    learningCourseVariables
  );
  return _.get(learningCourseDetails, "grades", []);
};

export const getLearningCourseSubjects = ({
  learningCourseId,
  academicYearId,
}) => {
  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId,
    academicYearId,
  });
  const learningCourse = getLearningCourseDetailsFromCache(
    learningCourseVariables
  );
  return _.get(learningCourse, "subjects", []);
};

export const getPlannerOrganizationResourcesOfNodeVariables = ({
  parentId,
  parentType,
  portalType,
}) => {
  return {
    id: parentId,
    type: parentType,
    orderBy: "SEQUENCE",
    orderByDirection: "ASC",
    portalType,
    first: null,
    filters: {
      types: ["PLANNER_RESOURCE"],
    },
  };
};

/**
 * @param {Object} template - unit plan template
 * @description - modifies the template to add a new section to the right pane of unit flow
 */
export const pushNewSectionToRightPane = _.memoize((template, item) => {
  if (!_.includes(_.get(template, "body.rightPane", []), item)) {
    return update(template, { body: { rightPane: { $push: [item] } } });
  }

  return template;
});

export const getIsProSchool = () => {
  const state = store.getState();
  const curriculumPrograms = _.get(
    state,
    "platform.organizationCurriculumPrograms",
    []
  );

  return _.every(curriculumPrograms, curriculumProgram =>
    _.includes(
      [PLAN_TYPE_PRO, PLAN_TYPE_PRO_PILOT],
      _.get(curriculumProgram, "subscriptionPlan.type")
    )
  );
};

export const getElementLabel = ({ type, value, isPypElement = true }) => {
  if (value && !Array.isArray(value)) {
    const element = getPlannerElementFromCache({
      type,
      id: value,
      isPypElement,
    });

    return _.get(element, "label", "N/A");
  } else {
    return _.filter(
      _.map(value, item => {
        const element = getPlannerElementFromCache({
          type,
          id: item,
          isPypElement,
        });
        return _.get(element, "label");
      }),
      val => !!val
    );
  }
};

// -------------------------------------------------------------------------------

export const getSubjectsFromDataSetNodeWrapper = node => {
  return _.get(node, "dataSetNodeWrapper.properties.subjects", []);
};

export const getUniqSubjectsFromNodes = ({ nodes }) => {
  return _.values(
    _.reduce(
      nodes,
      (result, node) => {
        if (checkIsNodeTopParent(node)) {
          const subjects = getSubjectsFromDataSetNodeWrapper(node);
          _.forEach(subjects, subject => {
            if (subject && subject.id && !result[subject.id]) {
              result[subject.id] = subject;
            }
          });
        }
        return result;
      },
      {}
    )
  );
};

export const mergeSubjectsWithNodes = ({ nodes, subjectNodes }) => {
  const resultNodes = [];

  _.forEach(nodes, node => {
    const updatedNode = { ...node };
    if (checkIsNodeTopParent(node)) {
      const nodeSubjects = getSubjectsFromDataSetNodeWrapper(node);

      if (!_.isEmpty(nodeSubjects)) {
        const firstMatchingSubject = _.first(
          _.intersectionBy(subjectNodes, nodeSubjects, "id")
        );
        updatedNode["parent"] = _.get(firstMatchingSubject, "id", null);
      }
    }
    resultNodes.push(updatedNode);
  });

  _.forEach(subjectNodes, subjectNode => {
    resultNodes.push(subjectNode);
  });

  return resultNodes;
};

const plannerElementValue = ({
  value,
  fieldObj,
  subjectIds,
  resolvedValue,
  deriveSubjectIdsFromNodes = false,
  skipRemoveSubjectCheck = false,
  skipMergeStandardsAssociatedParents = false,
}) => {
  const nodes = _.get(resolvedValue, "nodes", []);
  const config = _.get(fieldObj, "config", {});
  const dynamicSubject = _.get(fieldObj, "dynamicSubject", {});

  const plannerElementConfig = _.get(config, "plannerElementConfig", {}) || {};
  const { type } = plannerElementConfig;

  // Following condition is generic to handle nested element or PlannerElementDataSet
  if (
    _.get(fieldObj, "viewType") === "Nestedview" ||
    _.get(fieldObj, "viewType") === "PlannerElementDataSet"
  ) {
    let subjectNodes = getUniqSubjectsFromNodes({ nodes });
    let updatedNodes = nodes;

    if (_.isEmpty(subjectIds) && deriveSubjectIdsFromNodes) {
      subjectIds = _.map(
        _.filter(subjectNodes, item => _.get(item, "__typename") == "Subject"),
        item => item.id
      );
    }

    // Following condition is given to remove the subjects which are selected but removed afterwards
    if (
      _.includes(["MYP_LEARNING_STANDARD", "UBD_LEARNING_STANDARD"], type) &&
      !skipRemoveSubjectCheck
    ) {
      const subjects = _.map(subjectIds, subjectId => {
        return { id: subjectId };
      });

      const updatedValues = _.filter(updatedNodes, updatedNode => {
        const nodeSubjects = _.get(
          updatedNode,
          "dataSetNodeWrapper.properties.subjects",
          []
        );
        return _.intersectionBy(subjects, nodeSubjects, "id").length > 0;
      });

      updatedNodes = updatedValues;
    }

    if (
      _.includes(
        [
          "MYP_RELATED_CONCEPT",
          "MYP_SPECIFIED_CONCEPTS",
          "MYP_LEARNING_STANDARD",
          "MYP_ATL",
          "UBD_LEARNING_STANDARD",
          "UBD_ATL",
          "DP_ATL",
        ],
        type
      )
    ) {
      let filteredSubjectIds = subjectIds;

      if (!_.isEmpty(dynamicSubject)) {
        filteredSubjectIds = [_.get(fieldObj, "dynamicSubject.id", "")];
      }

      if (!_.isEmpty(filteredSubjectIds)) {
        subjectNodes = _.filter(subjectNodes, node =>
          _.includes(filteredSubjectIds, node.id)
        );
      }
    }

    if (
      _.size(subjectNodes) > 1 &&
      !_.isEqual(type, "UBD_LEARNING_STANDARD") &&
      !skipMergeStandardsAssociatedParents
    ) {
      updatedNodes = mergeSubjectsWithNodes({
        subjectNodes,
        nodes: updatedNodes,
      });
    }

    return generateNestedSelectedData({
      ...getPYPElementSetFromNodes({ nodes: updatedNodes }),
      value,
    });
  } else {
    if (
      _.includes(
        [
          "DP_CAS_STRANDS",
          "DP_CAS_SERVICE_TYPES",
          "DP_CP_SL_APPROACHES",
          "DP_CAS_APPROACHES",
          "DP_CP_SL_STRANDS",
          "MYP_SERVICE_ACTION_TYPE",
        ],
        type
      )
    ) {
      const hasDisplaySeq = !_.some(
        nodes,
        node => !_.get(node, "displaySequence", null)
      );

      if (hasDisplaySeq) {
        const updatedValue = value
          ? _.orderBy(
              value,
              item => _.find(nodes, { id: item })?.displaySequence
            )
          : value;
        return getPlannerElementLabel({ nodes, value: updatedValue });
      } else {
        const updatedValue = value ? value.sort() : value;
        return getPlannerElementLabel({ nodes, value: updatedValue });
      }
    }
    return getPlannerElementLabel({ nodes, value });
  }
};

export const getElementValue = ({
  fieldUID,
  valueKeys,
  fieldObj,
  resolvedValue,
  subjectIds,
  skipRemoveSubjectCheck = false,
  skipMergeStandardsAssociatedParents = false,
  deriveSubjectIdsFromNodes = false,
}) => {
  switch (fieldUID) {
    case "subjects":
      return _.map(
        _.get(resolvedValue, "subjects", []),
        subject => subject.name
      );

    case "theme":
    case "concepts":
    case "lp": {
      return getElementLabel({
        type: pypElementMapping[fieldUID],
        value: valueKeys,
      });
    }
    case "learnerProfileDP": {
      return getElementLabel({
        type: pypElementMapping[fieldUID],
        value: valueKeys,
        isPypElement: false,
      });
    }

    case "atls":
      return getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "atls", []),
      });

    case "benchmarks": {
      if (!_.isEmpty(subjectIds) && !skipRemoveSubjectCheck) {
        const benchmarks = _.get(resolvedValue, "benchmarks", []);
        const filteredBenchmarks = _.filter(benchmarks, item =>
          _.includes(subjectIds, item.subject)
        );

        return getBenchMarkValueMemoized({
          values: valueKeys,
          nodes: filteredBenchmarks,
        });
      }

      if (skipRemoveSubjectCheck) {
        const sortedBenchmarks = _.orderBy(
          _.get(resolvedValue, "benchmarks", []),
          [item => _.get(item, "standardsWrapper.standardSet.id")],
          ["asc"]
        );

        return getBenchMarkValueMemoized({
          values: valueKeys,
          nodes: sortedBenchmarks,
        });
      }

      return getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "benchmarks", []),
      });
    }

    // DP Fields
    case "subjectLevel":
      return _.map(_.get(resolvedValue, "genericTags", []), tag => tag.label);

    case "syllabusContentDP":
    case "atlDP":
    case "subjectStandardDP":
      return getBenchMarkValueMemoized({
        values: valueKeys,
        nodes: _.get(resolvedValue, "nodes", []),
        shouldGenerateParentChildRelation: false,
      });

    default:
      if (_.get(fieldObj, "config.type") === "PLANNER_ELEMENT") {
        return plannerElementValue({
          fieldObj,
          subjectIds,
          resolvedValue,
          value: valueKeys,
          skipRemoveSubjectCheck,
          skipMergeStandardsAssociatedParents,
          deriveSubjectIdsFromNodes,
        });
      }
      return valueKeys;
  }
};

export const getFieldContextFromParentType = ({ parentType }) => {
  switch (parentType) {
    case "PROJECT":
      return FieldComponentContext;
    default:
      return null;
  }
};

export const getStringWithArchivedLabel = ({ isArchived, t }) => {
  return isArchived ? ` (${t("common:archived")})` : ``;
};

export const getUnitPlanCardThemeTextArr = ({ data, key, t }) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return _.map(data, item => {
    const isArchived = _.get(item, "isArchived", false);
    return item[key] + getStringWithArchivedLabel({ isArchived, t });
  });
};
